import { CommonModule } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { FilestackModule } from "@filestack/angular";
import { ClipboardModule } from "@angular/cdk/clipboard";
import { NgxPlaidLinkModule } from "ngx-plaid-link";
import { CustomFieldComponent } from "../dashboard/bankAccounts/components/edit-bank-account/custom-field/custom-field.component";
import { IbanFieldComponent } from "../dashboard/bankAccounts/components/edit-bank-account/iban-field/iban-field.component";
import { IsraelBankSelectFieldComponent } from "../dashboard/bankAccounts/components/edit-bank-account/israel-bank-select-field/israel-bank-select-field.component";
import { SwiftFieldComponent } from "../dashboard/bankAccounts/components/edit-bank-account/swift-field/swift.field.component";
import { UsRoutingNumberFieldComponent } from "../dashboard/bankAccounts/components/edit-bank-account/us-routing-number-field/us-routing-number-field.component";
import { HoldingImagesComponent } from "../dashboard/shared/holding/holding-images/holding-images.component";
import { AddressFormComponent } from "./components/address-form/address-form.component";
import { AlertDialogComponent } from "./components/alert-dialog/alert-dialog.component";
import { ClientBankAccountDetailsComponent } from "./components/client-bank-account-details-dialog/client-bank-account-details-dialog.component";
import { ClientBankAccountComponent } from "./components/client-bank-account/client-bank-account.component";
import { DistributionRatesCompareTableComponent } from "./components/client-bank-account/distribution-rates-compare-table/distribution-rates-compare-table.component";
import { ConfirmDialogComponent } from "./components/confirm-dialog/confirm-dialog.component";
import { ConfirmRedirectDialogComponent } from "./components/confirm-redirect-dialog/confirm-redirect-dialog.component";
import { CountryAutoCompleteComponent } from "./components/country-auto-complete/country-auto-complete.component";
import { DialogHeaderComponent } from "./components/dialog-header/dialog-header.component";
import { DocumentThumbnailComponent } from "./components/document-thumbnail/document-thumbnail.component";
import { FilePreviewDialogComponent } from "./components/file-preview-dialog/file-preview-dialog.component";
import { FilePreviewPageComponent } from "./components/file-preview-page/file-preview-page.component";
import { FileStackPickerDialogComponent } from "./components/file-stack-picker-dialog/file-stack-picker-dialog.component";
import { FileUploadComponent } from "./components/file-upload/file-upload.component";
import { FilesSelectorComponent } from "./components/files-selector/files-selector.component";
import { ImageSliderDotsComponent } from "./components/image-slider-dots/image-slider-dots.component";
import { ImageUploaderThumbnailComponent } from "./components/image-uploader-thumbnail/image-uploader-thumbnail.component";
import { MainNavComponent } from "./components/main-nav/main-nav.component";
import { MaintenanceComponent } from "./components/maintenance/maintenance.component";
import { MediaThumbnailComponent } from "./components/media-thumbnail/media-thumbnail.component";
import { PhoneComponent } from "./components/phone/phone.component";
import { DisableControlDirective } from "./directives/disable-control.directive";
import { ForceAutoCompleteSelectionDirective } from "./directives/force-auto-complete-selection.directive";
import { HoverClassDirective } from "./directives/hover-class.directive";
import { MyCurrencyFormatterDirective } from "./directives/numberFormatter.directive";
import { TabHeightFixDirective } from "./directives/tab-height-fix.directive";
import { LoaderComponent } from "./loader/loader.component";
import { EnumStringPipe } from "./pipes/EnumToString.pipe";
import { FileSizePipe } from "./pipes/FileSize.pipe";
import { MediaFileIconPipe } from "./pipes/mediaFileIcon.pipe";
import { PhoneNumberPipe } from "./pipes/PhoneNumber.pipe";
import { SafeHtmlPipe } from "./pipes/SafeHtml.pipe";
import { TerraCurrencyNoSymbolPipe } from "./pipes/TerraCurrency.pipe";
import { TerraNumberPipe } from "./pipes/TerraNumber.pipe";
import { TimeAgoPipe } from "./pipes/time-ago.pipe";
import { TruncatePipe } from "./pipes/TruncatePipe.pipe";
import { UnitNumberPipe } from "./pipes/UnitNumber.pipe";
import { LpDesktopNavComponent } from "../lp/shared/lp-desktop-nav/lp-desktop-nav.component";
import { LpMobileNavComponent } from "../lp/shared/lp-mobile-nav/lp-mobile-nav.component";
import { AccountActionsComponent } from "./components/account-actions/account-actions.component";
import { ApproveTermsAndConditionsDialogComponent } from "./components/approve-terms-and-conditions/approve-terms-and-conditions-dialog.component";
import { CountryInputComponent } from "./components/country-input/country-input.component";
import { BankAccountNumberComponent } from "./components/dynamic-component-loader/bank-account-number.component";
import { CheckboxComponent } from "./components/dynamic-component-loader/checkbox.component";
import { HighHolidaysNotificationComponent } from "./components/high-holidays-notification/high-holidays-notification.component";
import { ImageSliderComponent } from "./components/image-slider/image-slider.component";
import { NoAccessComponent } from "./components/no-access/no-access.component";
import { OauthComponent } from "./components/oauth/oauth.component";
import { PhoneVerificationDialogComponent } from "./components/phone-verification-dialog/phone-verification-dialog.component";
import { RecipientContactsDisplayTableComponent } from "./components/recipient-contacts-display-table/recipient-contacts-display-table.component";
import { SixDigitCodeInput } from "./components/six-digit-code-input/six-digit-code-input.component";
import { StickyFooterComponent } from "./components/sticky-footer/sticky-footer.component";
import { SupportComponent } from "./components/support/support.component";
import { BankAccountLimitsComponent } from "./components/unit/bank-account-limits/bank-account-limits.component";
import { LimitLineItemComponent } from "./components/unit/bank-account-limits/limit-line-item/limit-line-item.component";
import { TransferUsingPlaidDialogComponent } from "./components/unit/transfer-using-plaid-dialog/transfer-using-plaid-dialog.component";
import { UnitApplicationComponent } from "./components/unit/unit-application/unit-application.component";
import { UnitDocumentConfirmDialogComponent } from "./components/unit/unit-application/unit-document-confirm-dialog/unit-document-confirm-dialog.component";
import { UnitBankAccountActionsComponent } from "./components/unit/unit-bank-account-actions/unit-bank-account-actions.component";
import { UnitBankAccountFeesTableComponent } from "./components/unit/unit-bank-account-fees-table/unit-bank-account-fees-table.component";
import { UnitBankAccountStatementComponent } from "./components/unit/unit-bank-account-statement/unit-bank-account-statement.component";
import { UnitBankAccountVerificationLetterComponent } from "./components/unit/unit-bank-account-verification-letter/unit-bank-account-verification-letter.component";
import { UnitBankAccountPendingApprovalComponent } from "./components/unit/unit-bank-account/unit-bank-account-pending-approval/unit-bank-account-pending-approval.component";
import { UnitBankAccountComponent } from "./components/unit/unit-bank-account/unit-bank-account.component";
import { UnitCreateBankAccountFailedComponent } from "./components/unit/unit-create-bank-account/unit-create-bank-account-failed/unit-create-bank-account-failed.component";
import { UnitCreateBankAccountFillDetailsStepComponent } from "./components/unit/unit-create-bank-account/unit-create-bank-account-fill-details-step/unit-create-bank-account-fill-details-step.component";
import { UnitCreateBankAccountReviewStepComponent } from "./components/unit/unit-create-bank-account/unit-create-bank-account-review-step/unit-create-bank-account-review-step.component";
import { UnitCreateBankAccountComponent } from "./components/unit/unit-create-bank-account/unit-create-bank-account.component";
import { UnitCreateEditApplicationComponent } from "./components/unit/unit-create-edit-application/unit-create-edit-application.component";
import { SelectExistingRecipientDialogComponent } from "./components/unit/unit-payment/unit-create-edit-payment/select-existing-recipient-dialog/select-existing-recipient-dialog.component";
import { UnitCreateEditPaymentComponent } from "./components/unit/unit-payment/unit-create-edit-payment/unit-create-edit-payment.component";
import { UnitPaymentConfirmationComponent } from "./components/unit/unit-payment/unit-payment-confirmation/unit-payment-confirmation.component";
import { UnitPaymentDetailsComponent } from "./components/unit/unit-payment/unit-payment-details/unit-payment-details.component";
import { UnitPaymentReviewKeyValuePairComponent } from "./components/unit/unit-payment/unit-payment-overview-key-value-pair/unit-payment-review-key-value-pair.component";
import { UnitPaymentRecipientDetailsComponent } from "./components/unit/unit-payment/unit-payment-recipient-details/unit-payment-recipient-details.component";
import { UnitPaymentReviewComponent } from "./components/unit/unit-payment/unit-payment-review/unit-payment-review.component";
import { UnitPaymentSenderDetailsComponent } from "./components/unit/unit-payment/unit-payment-sender-details/unit-payment-sender-details.component";
import { UnitPaymentTypeModalComponent } from "./components/unit/unit-payment/unit-payment-type-modal/unit-payment-type-modal.component";
import { UnitRecipientPaymentsHistoryDialogComponent } from "./components/unit/unit-payment/unit-recipient-payments-history/unit-recipient-payments-history-dialog/unit-recipient-payments-history-dialog.component";
import { UnitRecipientPaymentsHistoryDisplayTableComponent } from "./components/unit/unit-payment/unit-recipient-payments-history/unit-recipient-payments-history-display-table/unit-recipient-payments-history-display-table.component";
import { UnitTransactionsListComponent } from "./components/unit/unit-transactions-list/unit-transactions-list.component";
import { AddDynamicComponentDirective } from "./directives/add-dynamic-component.directive";
import { CheckPermissionsDirective } from "./directives/check-permissions.directive";
import { DisableLinkDirective } from "./directives/disable-link.directive";
import { HeaderLessTabsDirective } from "./directives/header-less-tabs.directive";
import { MaterialModule } from "./material.module";
import { AccountNumberToAstrixPipe } from "./pipes/account-number-to-astrix.pipe";
import { BankAccountDisplayName } from "./pipes/BankAccountDisplayName.pipe";
import { CountryCodePipe } from "./pipes/countryCode.pipe";
import { HideUsdIsoPipe } from "./pipes/hide-usd-iso.pipe";
import { FedwireRoutingNumberFieldComponent } from "../dashboard/bankAccounts/components/edit-bank-account/fedwire-routing-number/fedwire-routing-number-field.component";
import { ExternalBankAccountActionsComponent } from "../dashboard/bankAccounts/components/external-bank-account-actions/external-bank-account-actions.component";
import { BankAccountComponent } from './components/bank-account/bank-account.component';
import { ButtonsComponent } from "./components/buttons/buttons.component";
import { ChipsStatusComponent } from "./components/chips-status/chips-status.component";
import { CovercyFooterComponent } from "./components/covercy-footer/covercy-footer.component";
import { DatePickerYearComponent } from "./components/date-picker-year/date-picker-year.component";
import { DateRangePickerDialogComponent } from "./components/date-range-picker-dialog/date-range-picker-dialog.component";
import { DateRangePickerComponent } from "./components/date-range-picker/date-range-picker.component";
import { DownloadFileComponent } from "./components/download-file/download-file.component";
import { NoAccessPermissionComponent } from "./components/dynamic-component-loader/no-access-permission.component";
import { DynamicDialogComponent } from "./components/dynamic-dialog/dynamic-dialog.component";
import { ExportTransactionsMenuComponent } from "./components/export-transactions-menu/export-transactions-menu.component";
import { RadioButtonsComponent } from "./components/form/radio-buttons/radio-buttons.component";
import { IconTextComponent } from "./components/icon/icon-text/icon-text.component";
import { InformationHintComponent } from "./components/information-hint/information-hint.component";
import { InvestorComponent } from "./components/investor/investor.component";
import { NameIconComponent } from "./components/name-icon/name-icon.component";
import { OptionItemComponent } from "./components/option-item/option-item.component";
import { PhoneCountryPickerModule } from "./components/phone-country-picker/phone-country-picker.module";
import { PlaidTransactionsComponent } from './components/plaid-transactions/plaid-transactions.component';
import { SearchFieldComponent } from "./components/search-field/search-field.component";
import { SnackbarMessageComponent } from "./components/snackbar-message/snackbar-message.component";
import { TransactionsListComponent } from './components/transactions-list/transactions-list/transactions-list.component';
import { UnitBankAccountLimitDialogComponent } from "./components/unit/bank-account-limits/unit-bank-account-limit-dialog/unit-bank-account-limit-dialog.component";
import { UnitBankAccountDocumentComponent } from "./components/unit/unit-bank-account-document/unit-bank-account-document.component";
import { UnitBankStickyFooterComponent } from "./components/unit/unit-bank-sticky-footer/unit-bank-sticky-footer.component";
import { UnitCardConfirmDialogContentComponent } from "./components/unit/unit-card/confirm-dialog-content/unit-card-confirm-dialog-content.component";
import { CreateCardDialogComponent } from "./components/unit/unit-card/create-card-dialog/create-card-dialog.component";
import { UnitCardActionsComponent } from "./components/unit/unit-card/unit-card-actions/unit-card-actions.component";
import { UnitCardCreateCustomerTokenComponent } from "./components/unit/unit-card/unit-card-create-customer-token/unit-card-create-customer-token.component";
import { UnitCardEmptyComponent } from "./components/unit/unit-card/unit-card-empty/unit-card-empty.component";
import { UnitCardFinishComponent } from "./components/unit/unit-card/unit-card-finish/unit-card-finish.component";
import { UnitCardHolderFormComponent } from "./components/unit/unit-card/unit-card-holder-form/unit-card-holder-form.component";
import { UnitCardPinDialogComponent } from "./components/unit/unit-card/unit-card-pin-dialog/unit-card-pin-dialog.component";
import { UnitCardComponent } from "./components/unit/unit-card/unit-card/unit-card.component";
import { UnitCardsSectionComponent } from "./components/unit/unit-card/unit-cards-section/unit-cards-section.component";
import { UnitCardsSliderComponent } from "./components/unit/unit-card/unit-cards-slider/unit-cards-slider.component";
import { SystemFeatureFlagDirective } from "./directives/system-feature-flag.directive";
import { TrimDirective } from "./directives/trim.directive";
import { EnumToIcon } from "./pipes/EnumToIcon.pipe";
import { HoldingStatusArrayToStringPipe } from "./pipes/holding-status-array-to-string.pipe";
import { LocationToStringPipe } from "./pipes/location-to-string.pipe";
import { SafeUrlPipe } from "./pipes/SafeUrl.pipe";
import { ShortScaleNumberPipe } from "./pipes/ShortScaleNumber.pipe";
import { TimesincePipe } from './pipes/timesince.pipe';
import { PrimengModule } from "./primeng.module";
import { QuillConfig, QuillModule, QuillToolbarConfig } from "ngx-quill";
import DirectionModule from "src/plugins/quill-editor/rtl-extension.plugin";
import CustomAlignModule from "src/plugins/quill-editor/align-extension.plugin";
import { CustomPropertiesComponent } from "./components/custom-properties/custom-properties.component";
import { CustomUtcDatePickerComponent } from "./components/custom-utc-date-picker/custom-utc-date-picker.component";
import { FileImportUploadComponentimplements } from "./components/file-import-upload/file-import-upload.component";
import { HttpService } from "../services/http.service";
import AiActionsButtonModule from "../../plugins/quill-editor/ai-actions/ai-actions-button.plugin";
import { AppSettingsService } from "../services/app-settings.service";
import { UserService } from '../services/shared/user.service';
import { DistributionReasonsForTransactionToStringPipe } from './pipes/distribution-reasons-for-transaction-to-string.pipe';
import { UtcDatePipe } from "./pipes/utc-date.pipe";
import {StorageService} from "../services/storage.service";
import { TelemetryService } from "telemetry-library";
import Quill from 'quill'
import ImageResizor from 'quill-image-resizor'
import { ImageEnabledQuillEditorComponent } from "./components/image-enabled-quill-editor/image-enabled-quill-editor.component";

// If Quill does not come from window-object -> set find method to correct instance
ImageResizor.Quill = Quill;
Quill.register('modules/imageResizor', ImageResizor)

const QUILL_TOOLBAR_OPTIONS = {
  container: [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'link'],
    [{'list': 'ordered'}, {'list': 'bullet'}],
    [{'direction': 'rtl'}, {'align': []}],        // text direction
    [{'header': [1, 2, 3, 4, false]}],
    [{'color': []}, {'background': []}],          // dropdown with defaults from theme
    // ['clean'] // not cleanning all style attributs from the text
    [{'ai-actions': [
        'placeholder-to-make-no-selection-on-first-interaction',
        'improve',
        'formalize',
        'make-longer',
        'make-shorter',
        'help-me-write',
      ]}],
  ] as QuillToolbarConfig,
  handlers: {
    'ai-actions': () => {},
  }
};

export const QuillDefaultConfig: QuillConfig = {
  modules: {
    toolbar: QUILL_TOOLBAR_OPTIONS,
    directionModule: DirectionModule,
    customAlignModule: CustomAlignModule,
    aiActionsButton: AiActionsButtonModule,
    imageResizor: {}
  },
  customModules: [
  ],
  customOptions: [
  ]
};

const UNIT_CARD = [
  CreateCardDialogComponent,
  UnitCardHolderFormComponent,
  UnitCardEmptyComponent,
  UnitCardFinishComponent,
  UnitCardComponent,
  UnitCardsSectionComponent,
  UnitCardsSliderComponent,
  UnitCardActionsComponent,
  UnitCardCreateCustomerTokenComponent,
  UnitCardConfirmDialogContentComponent,
  UnitCardPinDialogComponent,
];

const UNIT_PAYMENT = [
  UnitCreateEditPaymentComponent,
  SelectExistingRecipientDialogComponent,
  UnitPaymentConfirmationComponent,
  UnitPaymentDetailsComponent,
  UnitPaymentReviewKeyValuePairComponent,
  UnitPaymentRecipientDetailsComponent,
  UnitPaymentReviewComponent,
  UnitPaymentSenderDetailsComponent,
  UnitPaymentTypeModalComponent,
  UnitRecipientPaymentsHistoryDialogComponent,
  UnitRecipientPaymentsHistoryDisplayTableComponent,
  UnitBankAccountVerificationLetterComponent,
];

const UNIT_COMPONENT = [
  BankAccountLimitsComponent,
  LimitLineItemComponent,
  TransferUsingPlaidDialogComponent,
  UnitCreateEditApplicationComponent,
  UnitApplicationComponent,
  UnitDocumentConfirmDialogComponent,
  UnitCreateBankAccountComponent,
  UnitCreateBankAccountFailedComponent,
  UnitCreateBankAccountFillDetailsStepComponent,
  UnitCreateBankAccountReviewStepComponent,
  UnitBankAccountFeesTableComponent,
  UnitBankAccountComponent,
  UnitBankAccountPendingApprovalComponent,
  UnitBankAccountActionsComponent,
  UnitTransactionsListComponent,
  UnitBankAccountStatementComponent,
  UnitBankAccountDocumentComponent,
  UnitBankStickyFooterComponent,
  UnitBankAccountLimitDialogComponent,
  ...UNIT_PAYMENT,
  ...UNIT_CARD,
];

const SHARED_COMPONENT = [
  DownloadFileComponent,
  DynamicDialogComponent,
  NameIconComponent,
  IconTextComponent,
  InformationHintComponent,
  OptionItemComponent,
  SnackbarMessageComponent,
  TransactionsListComponent,
  InvestorComponent
];

const STANDALONE_COMPONENT = [
  DialogHeaderComponent,
  ButtonsComponent,
  LoaderComponent,
  SearchFieldComponent,
  RadioButtonsComponent,
  ExternalBankAccountActionsComponent,
  CustomUtcDatePickerComponent,
  FileImportUploadComponentimplements,
  CustomPropertiesComponent
];

const STANDALONE_DIRECTIVE = [
  CheckPermissionsDirective];

const STANDALONE_PIPE = [
  EnumStringPipe, AccountNumberToAstrixPipe, TimesincePipe, UtcDatePipe, DistributionReasonsForTransactionToStringPipe];

@NgModule({
  declarations: [
    MainNavComponent,
    AccountActionsComponent,
    EnumToIcon,
    LocationToStringPipe,
    TerraNumberPipe,
    FileSizePipe,
    MediaFileIconPipe,
    MyCurrencyFormatterDirective,
    FileUploadComponent,
    ImageUploaderThumbnailComponent,
    PhoneComponent,
    SixDigitCodeInput,
    CustomFieldComponent,
    SwiftFieldComponent,
    IsraelBankSelectFieldComponent,
    IbanFieldComponent,
    UsRoutingNumberFieldComponent,
    FedwireRoutingNumberFieldComponent,
    ClientBankAccountComponent,
    ConfirmRedirectDialogComponent,
    ConfirmDialogComponent,
    AlertDialogComponent,
    TruncatePipe,
    BankAccountDisplayName,
    AddressFormComponent,
    // DialogHeaderComponent,
    TerraCurrencyNoSymbolPipe,
    UnitNumberPipe,
    PhoneNumberPipe,
    HoldingImagesComponent,
    MediaThumbnailComponent,
    DocumentThumbnailComponent,
    MaintenanceComponent,
    FilesSelectorComponent,
    HoverClassDirective,
    TabHeightFixDirective,
    ForceAutoCompleteSelectionDirective,
    CountryAutoCompleteComponent,
    CountryInputComponent,
    ImageSliderComponent,
    ImageSliderDotsComponent,
    ClientBankAccountDetailsComponent,
    SafeHtmlPipe,
    SafeUrlPipe,
    FileStackPickerDialogComponent,
    FilePreviewDialogComponent,
    FilePreviewPageComponent,
    TimeAgoPipe,
    DisableControlDirective,
    DisableLinkDirective,
    DistributionRatesCompareTableComponent,
    HighHolidaysNotificationComponent,
    CountryCodePipe,
    PhoneVerificationDialogComponent,
    StickyFooterComponent,
    HideUsdIsoPipe,
    AddDynamicComponentDirective,
    CheckboxComponent,
    NoAccessPermissionComponent,
    BankAccountNumberComponent,
    StickyFooterComponent,
    OauthComponent,
    HeaderLessTabsDirective,
    ApproveTermsAndConditionsDialogComponent,
    NoAccessComponent,
    // CheckPermissionsDirective,
    RecipientContactsDisplayTableComponent,
    ...UNIT_COMPONENT,
    SupportComponent,
    LpMobileNavComponent,
    LpDesktopNavComponent,
    ExportTransactionsMenuComponent,
    DateRangePickerComponent,
    DateRangePickerDialogComponent,
    DatePickerYearComponent,
    ChipsStatusComponent,
    TrimDirective,
    ShortScaleNumberPipe,
    SystemFeatureFlagDirective,
    CovercyFooterComponent,
    ...SHARED_COMPONENT,
    HoldingStatusArrayToStringPipe,
    BankAccountComponent,
    PlaidTransactionsComponent,
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    FilestackModule,
    MaterialModule,
    NgxPlaidLinkModule,
    ClipboardModule,
    PrimengModule,
    PhoneCountryPickerModule,
    ...STANDALONE_COMPONENT,
    ...STANDALONE_DIRECTIVE,
    ...STANDALONE_PIPE,
    // TelemetryLibraryModule,
    // PdfViewerModule
    QuillModule.forRoot(QuillDefaultConfig),
    ImageEnabledQuillEditorComponent
  ],
  exports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    FilestackModule,
    ClipboardModule,
    QuillModule,
    // PdfViewerModule,
    ...STANDALONE_COMPONENT,
    ...STANDALONE_DIRECTIVE,
    ...STANDALONE_PIPE,
    MainNavComponent,
    AccountActionsComponent,
    MyCurrencyFormatterDirective,
    DisableControlDirective,
    DisableLinkDirective,
    FileUploadComponent,
    FilesSelectorComponent,
    ImageUploaderThumbnailComponent,
    PhoneComponent,
    SixDigitCodeInput,
    CustomFieldComponent,
    SwiftFieldComponent,
    IsraelBankSelectFieldComponent,
    IbanFieldComponent,
    UsRoutingNumberFieldComponent,
    ClientBankAccountComponent,
    ConfirmDialogComponent,
    FilePreviewDialogComponent,
    AddressFormComponent,
    // DialogHeaderComponent,
    ImageSliderComponent,
    ImageSliderDotsComponent,
    StickyFooterComponent,
    // Pipes
    TruncatePipe,
    BankAccountDisplayName,
    LocationToStringPipe,
    TerraNumberPipe,
    SafeHtmlPipe,
    SafeUrlPipe,
    TerraCurrencyNoSymbolPipe,
    FileSizePipe,
    MediaFileIconPipe,
    TimeAgoPipe,
    CountryCodePipe,
    UnitNumberPipe,
    PhoneNumberPipe,
    HideUsdIsoPipe,
    HoldingStatusArrayToStringPipe,
    // End - Pipes
    HoldingImagesComponent,
    MediaThumbnailComponent,
    DocumentThumbnailComponent,
    HoverClassDirective,
    TabHeightFixDirective,
    ForceAutoCompleteSelectionDirective,
    CountryAutoCompleteComponent,
    CountryInputComponent,
    FileStackPickerDialogComponent,
    DistributionRatesCompareTableComponent,
    PhoneVerificationDialogComponent,
    HideUsdIsoPipe,
    HeaderLessTabsDirective,
    ApproveTermsAndConditionsDialogComponent,
    NoAccessComponent,
    AddDynamicComponentDirective,
    // CheckPermissionsDirective,
    TrimDirective,
    RecipientContactsDisplayTableComponent,
    ...UNIT_COMPONENT,
    SupportComponent,
    LpMobileNavComponent,
    LpDesktopNavComponent,
    ...UNIT_CARD,
    ExportTransactionsMenuComponent,
    DateRangePickerComponent,
    DateRangePickerDialogComponent,
    DatePickerYearComponent,
    ChipsStatusComponent,
    PrimengModule,
    ShortScaleNumberPipe,
    SystemFeatureFlagDirective,
    CovercyFooterComponent,
    PhoneCountryPickerModule,
    ...SHARED_COMPONENT,
    BankAccountComponent,
    ImageEnabledQuillEditorComponent
  ],
  providers: [
    TerraNumberPipe,
    CountryCodePipe,
    PhoneNumberPipe,
    HideUsdIsoPipe,
    HttpService,
  ],
  schemas: [ CUSTOM_ELEMENTS_SCHEMA ] // added for custom Swiper web element
})

export class CovercySharedComponentsModule {
  constructor(http: HttpService,
              appSettings: AppSettingsService,
              userService: UserService,
              storageService: StorageService,
              telemetryService: TelemetryService
               ) {
    // Since the Covercy AI Actions feature is implemented as a quill toolbar element,
    // and has no access to the angular ecosystem, we have to send in some vital services
    AiActionsButtonModule.http = http;
    AiActionsButtonModule.appSettings = appSettings;
    AiActionsButtonModule.userService = userService;
    AiActionsButtonModule.storageService = storageService;
    AiActionsButtonModule.telemetryService = telemetryService;
  }
}
